import React, {useState} from 'react';
import {Button, FormGroup, Panel} from 'react-bootstrap';
import {Cross} from '../misc/Cross';

export const TEXT_TYPE = 'TEXT_TYPE';

const supportedOperators = [
  'is',
  'is_not',
  'includes'
];
const operatorMap = {
  is: 'er',
  is_not: 'er ikke',
  includes: 'inneholder'
};
const multiuseOperators = [
  'includes'
];
const operatorContentfulMap = {
  is: '',
  is_not: '[ne]',
  includes: '[match]'
};

export const getReadableOperatorName = (operator) => {
  return operatorMap[operator] ?? 'ukjent operator';
};

export const createTextFilter = (fieldName) => {
  if (!fieldName || fieldName === '') {
    throw new Error('a new filter must have a field name.');
  }

  return {
    type: TEXT_TYPE,
    fieldName,
    supportedOperators,
    multiuseOperators
  };
};

export const renderTextFilter = (key = '', filterOptions = {}, removeCallback, editCallback) => {
  const {type, id, fieldName, displayName, selectedOperator, searchText} = filterOptions;
  if (type !== TEXT_TYPE) {
    return (
      <p style="color: red">ERROR: Text filter rendered with wrong type</p>
    );
  }

  return <TextFilter
    key={key}
    type={type}
    name={fieldName}
    id={id}
    operator={supportedOperators}
    searchText={searchText}
    displayName={displayName}
    removeCallback={removeCallback}
    editCallback={editCallback}
    selectedOperator={selectedOperator}
  />;
};

export const createContentfulTextFilterString = ({fieldName, selectedOperator, searchText}) => {
  const operatorString = operatorContentfulMap[selectedOperator] ?? null;

  // Return the filter as a tuple with the first item is the key and second is value.
  return [`${fieldName}${operatorString}`, searchText];
};

export const TextFilter = ({type, name, id, operator, searchText, displayName, removeCallback, editCallback, selectedOperator}) => {
  const [showEditDialogue, setShowEditDialogue] = useState(false);

  if (type !== TEXT_TYPE) {
    return (
      <p style="color: red">ERROR: Text filter rendered with wrong type</p>
    );
  }

  const editFilterCallback = (id, fieldName, type, options) => {
    editCallback(id, fieldName, type, options);
    setShowEditDialogue(false);
  };

  const humanReadableOperator = operatorMap[selectedOperator] ?? selectedOperator;
  const filterOptions = {
    type: type,
    fieldName: name,
    id: id,
    supportedOperators: operator
  };

  return (
    <div>
      <div className='filter-item' onClick={() => setShowEditDialogue(true)}>
        <span className='filter-item-inner'>
          <b>{displayName}</b> <i>{humanReadableOperator}</i> {searchText}
        </span>
        <a href="#" role="button" style={{padding: '0 5px'}} onClick={(e) => {
          e.preventDefault();
          removeCallback(id);
        }}>
          <Cross />
        </a>
      </div>
      <div id="edit-filter-backdrop" className={showEditDialogue ? 'invisible-backdrop' : ''} onClick={() => setShowEditDialogue(false)} />
      <Panel id="edit-filter" expanded={showEditDialogue} defaultExpanded={false} style={{
        position: 'absolute'
      }}>
        <Panel.Collapse>
          <Panel.Body style={{padding: 8}}>
            <div className='filter-panel-body'>
              <div>
                <label style={{display: 'flex', justifyContent: 'space-between'}}>
                  <b>Endre filter</b>
                  <a href="#" role="button" style={{padding: '0 5px'}} onClick={(e) => {
                    e.preventDefault();
                    setShowEditDialogue(false);
                  }}>
                    <Cross />
                  </a>
                </label>
              </div>
              <TextFilterEditDialogue displayName={displayName} preselectedOperator={selectedOperator} existingSearchText={searchText} editFilterCallback={editFilterCallback} filterOptions={filterOptions} />
            </div>
          </Panel.Body>
        </Panel.Collapse>
      </Panel>
    </div>
  );
};

export const TextFilterAddDialogue = ({displayName, filterCallback, filterOptions = {}}) => {
  const [selectedOperator, setSelectedOperator] = useState('');
  const [searchText, setSearchText] = useState('');
  const {type, fieldName, supportedOperators} = filterOptions;

  if (type !== TEXT_TYPE) {
    return (
      <p style="color: red">ERROR: Text filter rendered with wrong type</p>
    );
  }


  const onOperatorChange = (e) => {
    setSelectedOperator(e.target.value);
  };

  const addFilter = () => {
    filterCallback(fieldName, TEXT_TYPE, {
      displayName: displayName,
      selectedOperator,
      searchText
    });
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    addFilter();
  };

  return (
    <>
      <FormGroup>
        <select className="form-control" onChange={onOperatorChange}>
          <option value="">Velg operator</option>
          {supportedOperators.map((value) => (
            <option key={`available-filter-operator-${value}`} value={value}>{getReadableOperatorName(value)}</option>
          ))}
        </select>
      </FormGroup>
      <FormGroup>
        <form onSubmit={handleOnSubmit}>
          <input type="text" className="form-control" name="searchText" placeholder={'Søketekst'} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </form>
      </FormGroup>
      <Button bsStyle="primary" onClick={addFilter} disabled={!selectedOperator || searchText === ''}>
        Legg til filter
      </Button>
    </>
  );
};

export const TextFilterEditDialogue = ({displayName, preselectedOperator, existingSearchText, editFilterCallback, filterOptions = {}}) => {
  const [selectedOperator, setSelectedOperator] = useState(preselectedOperator);
  const [searchText, setSearchText] = useState(existingSearchText);
  const {fieldName, supportedOperators, id} = filterOptions;

  const onOperatorChange = (e) => {
    setSelectedOperator(e.target.value);
  };

  const editFilter = () => {
    editFilterCallback(id, fieldName, TEXT_TYPE, {
      displayName: displayName,
      selectedOperator,
      supportedOperators,
      searchText
    });
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    editFilter();
  };

  return (
    <>
      <FormGroup>
        <select className="form-control" defaultValue={selectedOperator} onChange={onOperatorChange}>
          {supportedOperators.map((value) => (
            <option key={`available-filter-operator-${value}`} value={value}>{getReadableOperatorName(value)}</option>
          ))}
        </select>
      </FormGroup>
      <FormGroup>
        <form onSubmit={handleOnSubmit}>
          <input type="text" className="form-control" name="searchText" placeholder={'Søketekst'} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </form>
      </FormGroup>
      <Button bsStyle="primary" onClick={editFilter} disabled={!selectedOperator || searchText === ''}>
        Oppdater filter
      </Button>
    </>
  );
};
